<template>
  <div class="customer-search">
    <SoForm ref="form" v-model="isValid" @submit="search">
      <SoRow>
        <SoCol cols="5">
          <SoSelect
            v-model="searchTerm"
            data-testid="select-searchterm"
            :items="customerSearch.searchTerms"
            type="simple"
          />
        </SoCol>
        <SoCol cols="5" class="px-0">
          <SoTextField
            :key="searchTerm"
            v-model="searchQuery"
            class="ml-2"
            :placeholder="getPlaceholder"
            :rules="getTextFieldRules"
            :hide-details="true"
            type="text"
          />
        </SoCol>
        <SoCol cols="auto">
          <SoButton
            :loading="isSearching"
            :disabled="isValid === false"
            icon="search"
            type="secondary"
            submit
            large
          />
        </SoCol>
      </SoRow>
    </SoForm>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { RouteNames } from "@/types/router"
  import {
    useCustomerSearchStore,
    CustomerSearchTerm,
  } from "@/stores/customerSearch"
  import { formatAccountNumber } from "@/helpers/accountHelper"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import rules from "@/validators/rules"
  export default defineComponent({
    components: {
      SoSelect,
      SoForm,
      SoTextField,
      SoButton,
      SoRow,
      SoCol,
    },
    data() {
      return {
        customerSearch: useCustomerSearchStore(),
        searchQuery: "",
        searchTerm: "account" as CustomerSearchTerm,
        isValid: false,
        rules: rules,
        shouldNavigateOnLoad: false,
      }
    },
    computed: {
      getTextFieldRules() {
        switch (this.searchTerm) {
          case "account":
            return [rules.accountNumber]
          case "email":
            return [rules.email, rules.required]
          case "postcode":
            return [rules.postcode, rules.required]
          case "telephone":
            return [rules.phoneNumber, rules.required]
          case "surname":
            return [rules.required]
          case "mpxn":
            return [rules.mpxn]
          default:
            return [rules.required]
        }
      },
      getPlaceholder() {
        return {
          account: "e.g. 00123456",
          postcode: "e.g. W4 5PY",
          email: "e.g. fred.flinstone@so.energy",
          telephone: "e.g. 012345 67890",
          surname: "e.g. Bond ...",
          mpxn: "e.g. 1234567890 (MPRN) or 21-digit MPAN",
        }[this.searchTerm]
      },
      isSearching() {
        return this.customerSearch.results?.isFetching ?? false
      },
      isAccountSearchTerm() {
        return this.searchTerm === "account"
      },
      isMpxnSearchTerm() {
        return this.searchTerm === "mpxn"
      },
    },
    watch: {
      "customerSearch.results.isFetching"(isFetching: boolean) {
        if (!isFetching && this.shouldNavigateOnLoad) {
          this.shouldNavigateOnLoad = false
          this.handleNavigationAfterSearch()
        }
      },
    },
    mounted() {
      this.searchQuery = this.customerSearch.query
      this.searchTerm = this.customerSearch.searchTerm
    },
    methods: {
      search() {
        const searchQuery = this.parseSearchQuery(this.searchQuery)

        this.customerSearch.search(this.searchTerm, searchQuery)
        if (this.isAccountSearchTerm || this.isMpxnSearchTerm) {
          this.shouldNavigateOnLoad = true

          if (
            this.customerSearch.results &&
            !this.customerSearch.results.isFetching
          ) {
            this.shouldNavigateOnLoad = false
            this.handleNavigationAfterSearch()
          }
        } else {
          this.$router.push({ name: RouteNames.CustomerSearch })
        }
      },
      parseSearchQuery(search: string) {
        return this.isAccountSearchTerm
          ? formatAccountNumber(search)
          : search.trim()
      },
      handleNavigationAfterSearch() {
        if (!this.customerSearch.results) return

        if (this.customerSearch.results.isSuccess && this.isAccountSearchTerm) {
          this.$router.push(`/customer/${this.customerSearch.query}`)
        } else if (this.isMpxnSearchTerm) {
          this.navigateToCustomerOrSearch()
        }
      },
      navigateToCustomerOrSearch() {
        if (
          !this.customerSearch.results ||
          this.customerSearch.results.data.length !== 1
        )
          this.$router.push({ name: RouteNames.CustomerSearch })
        else
          this.$router.push(
            `/customer/${this.customerSearch.results.data[0].number}`,
          )
      },
    },
  })
</script>
<style lang="css" scoped>
  .customer-search {
    max-width: 512px;
  }
  :deep(.v-card) {
    padding: 16px !important;
  }
  :deep(.v-field) {
    min-width: 186px;
  }
</style>
